import { useMemo } from "react"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import {
  useGetTagCoverageQuery,
  type TagCoverageCategory,
} from "~/ui-rtk/api/tagCategoriesApi"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * How much of the ad library each category actually covers.
 *
 * Rates, not raw counts, because raw counts mislead: a platform with 25x fewer
 * ads will always look "barely tagged" in absolute terms even when its rate
 * matches. Reading Danger Coffee's numbers as counts suggested TikTok was being
 * neglected; as rates it was within a few points of Meta, and the real story was
 * that hook coverage is thin on every platform.
 */

const pct = (tagged: number, total: number) =>
  total === 0 ? 0 : (100 * tagged) / total

/** Red below a third, amber below two thirds — a taxonomy nobody applies is worthless. */
const barColor = (value: number) => {
  if (value < 33) return "bg-system-error-500"
  if (value < 66) return "bg-system-yellow-500"
  return "bg-system-success-500"
}

function CoverageBar({ tagged, total }: { tagged: number; total: number }) {
  const value = pct(tagged, total)
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="h-2 flex-1 min-w-[60px] rounded-full bg-system-gray-800 overflow-hidden">
        <div
          className={cn("h-full rounded-full", barColor(value))}
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
      <span className="text-xs text-system-gray-300 tabular-nums w-28 shrink-0">
        {value.toFixed(1)}% ({tagged.toLocaleString()}/{total.toLocaleString()})
      </span>
    </div>
  )
}

function CategoryCoverage({ category }: { category: TagCoverageCategory }) {
  const { label, slug, taggedAds, totalAds, byPlatform } = category
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <H3 className="text-white font-bold text-base">
          {formatTagLabel(label)}
        </H3>
        <code className="text-xs text-system-gray-500">{slug}</code>
      </div>

      <CoverageBar tagged={taggedAds} total={totalAds} />

      <div className="flex flex-col gap-1.5 pl-2 border-l border-basic-blue/30">
        {byPlatform.map(p => (
          <div key={p.platform} className="flex items-center gap-3">
            <span className="text-xs text-system-gray-400 w-16 shrink-0">
              {p.platform}
            </span>
            <CoverageBar tagged={p.taggedAds} total={p.totalAds} />
          </div>
        ))}
      </div>
    </Card>
  )
}

export default function CoverageTab() {
  // Coverage moves when tags are applied anywhere (the tagging modal, MCP
  // agents), which no cache tag tracks — refetch whenever the tab is opened.
  const { data, isLoading, error } = useGetTagCoverageQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  // Worst coverage first: the point of this tab is finding the gaps, and a
  // category nobody has ever used should be the first thing you see.
  const sorted = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => pct(a.taggedAds, a.totalAds) - pct(b.taggedAds, b.totalAds),
      ),
    [data],
  )

  if (isLoading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader size={40} />
      </div>
    )
  }

  if (error) {
    return (
      <Card className="py-12 text-center">
        <P className="text-error">Could not load tag coverage.</P>
      </Card>
    )
  }

  const totalAds = sorted[0]?.totalAds ?? 0

  return (
    <div className="flex flex-col gap-system-md">
      <P className="text-sm text-system-gray-400">
        Share of the {totalAds.toLocaleString()} taggable ads carrying at least
        one value for each category. Deleted and archived ads are excluded —
        they can&rsquo;t be retagged, so counting them would only depress the
        numbers.
      </P>

      {sorted.length === 0 ? (
        <Card className="py-12 text-center">
          <P className="text-system-gray-400">
            No categories yet, so there is nothing to measure.
          </P>
        </Card>
      ) : (
        sorted.map(category => (
          <CategoryCoverage key={category.slug} category={category} />
        ))
      )}
    </div>
  )
}
